<template>
    <div class="form_flex_wrap loading_top_wrap">
        <div v-if="isLoading" class="loading_bg round_none transparent" style="z-index: 999;">
            <div class="loading_wrap">
                <div class="loading_obj">
                    <svg class="spinner" width="60px" height="60px" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                        <circle class="path" fill="none" stroke-width="6" stroke-linecap="round" cx="33" cy="33" r="30"></circle>
                    </svg>
                </div>
            </div>
        </div>
        <div class="form_flex_title">
            <span class="title">{{ $t('reservation_settings.section_staff_management.title') }}</span>
            <span class="info mgl_auto">{{ $t('reservation_settings.section_staff_management.sub_title') }}</span>
        </div>

        <div class="form_flex_cont" v-if="isEmptyGroup">
            <div class="no_cont">
                {{ $t('reservation_settings.section_staff_management.empty_group_message') }}
            </div>
        </div>

        <div class="form_flex_cont" v-if="!isEmptyGroup">
            <div class="rsv_group_tab">
                <GroupSelect v-for="(group, index) in originalGroups" :key="index"
                    :group="group"
                    :isSelected="selectedGroupIndex == index"
                    @selectGroup="selectGroup"/>
            </div>
            <table class="table2_default mgt_10">
                <colgroup>
                    <col>
                    <col width="140">
                    <col width="115">
                </colgroup>
                <thead>
                    <tr>
                        <th>{{ $t('reservation_settings.section_staff_management.label_staff') }}</th>
                        <th>{{ $t('reservation_settings.section_staff_management.label_change_order') }}</th>
                        <th>{{ $t('common.edit') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <ReservationColumnItem v-for="(column, index) in columns" :key="index"
                        :column="column"
                        :index="index"
                        :columns="columns"
                        @updateOrder="updateOrder"
                        @showColumnSettingPopup="showColumnSettingPopup"/>
                    <tr>
                        <td class="txt_center" colspan="3">
                            <button class="btn btn_s btn_blue_line mgl_auto" @click="showColumnSettingPopup(undefined)">＋ {{ $t('common.add') }}</button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <div class="btn_wrap">
            <span class="editor_info mgr_auto" v-if="!isEmptyGroup">{{ modifiedAt }} / {{ modifier }}</span>
            <button class="btn btn_l btn_blue mgl_auto" @click="saveReservationColumns()">{{ $t('common.save') }}</button>
        </div>
    </div>
</template>

<script>
const moment = require('@/util/MPMoment')

import { SpaceNavigationAction } from '../Enums/SpaceNavigationAction.js'

import GroupSelect from '../SubViews/GroupSelect.vue'
import ReservationColumnItem from '../SubViews/ReservationColumnItem.vue'

import { showColumnSetting } from '../../../../sheet-overlays'
import { useQueryClient } from '@tanstack/vue-query'
import { v2ReservationGroupListQueryOptions } from '@/composables/queries/v2reservationgroup/use-v2-reservationgroup-find-query'
import { v2ReservationSettingFindOneQueryOptions } from '@/composables/queries/v2reservationsetting/use-v2-reservationsetting-find-one-query'
import { useV2ReservationColumnUpdateSortMutation } from '@/composables/queries/v2reservationcolumn/use-v2-reservationcolumn-update-sort-mutation'
import { reservationColumnFindQueryOptions } from '@/composables/queries/reservationcolumn/use-reservation-column-find-query'

export default {
    setup() {
        const queryClient = useQueryClient()
        const { mutateAsync: updateColumnSort } = useV2ReservationColumnUpdateSortMutation()
        return { queryClient, updateColumnSort }
    },
    components: { GroupSelect, ReservationColumnItem },
    props: {
        groupUpdateTime: Object
    },
    data() {
        return {
            isLoading: false,
            isSaving: false,

            originalGroups: undefined,
            selectedGroup: undefined,
            settingValue: undefined,

            groupByColumn: {},
        }
    },
    watch: {
        groupUpdateTime: {
            handler: function() {
                this.initializeData()
            }
        }
    },
    computed: {
        modifiedAt: function() {
            if (this.settingValue?.columnModifiedAt == undefined) {
                return '-'
            } else {
                return moment(this.settingValue?.columnModifiedAt).toString(this.$t('common.date_format_YYYY_MM_DD_HH_mm'))
            }
        },
        modifier: function() {
            if (this.settingValue?.columnModifierID == undefined) {
                return '-'
            } else {
                if (this.settingValue.columnModifierID.type >= 3000) {
                    return this.$t('reservation_settings.section_staff_management.label_modified_afterdoc')
                } else {
                    return `${this.settingValue.columnModifierID.realName} ${this.$t('reservation_settings.section_staff_management.label_modified')}`
                }
            }
        },
        isEmptyGroup: function() {
            return (this.originalGroups?.length ?? 0) == 0
        },
        selectedGroupIndex: function() {
            if (this.originalGroups == undefined || this.selectedGroup == undefined) { return 0}

            let index = this.originalGroups.findIndex(g => g._id == this.selectedGroup._id)
            return index != -1 ? index : 0
        },
        columns: function(){
            return this.groupByColumn[this.selectedGroup?._id]

        }
    },
    mounted() {
        this.reqeustFetchReservationSetting()
        this.initializeData()
    },
    methods: {
        // 데이터 초기화
        async initializeData() {
            this.selectedGroup = undefined
            await this.reqeustFetchReservationGroup()
            this.requestFetchReservationColumns()
        },
        // 담당자 순서 변경
        updateOrder(action, index) {
            let column = this.columns.splice(index, 1)[0]
            switch(action) {
                case SpaceNavigationAction.FIRST:
                    this.columns.splice(0, 0, column)
                    break
                case SpaceNavigationAction.PREVIOUS:
                    this.columns.splice(index - 1, 0, column)
                    break
                case SpaceNavigationAction.NEXT:
                    this.columns.splice(index + 1, 0, column)
                    break
                case SpaceNavigationAction.LAST: 
                    this.columns.splice(this.columns.length, 0, column)
                    break
            }
        },
        // 그룹 선택
        selectGroup(group) {
            this.selectedGroup = group
        },
        // 컬럼 설정 팝업
        showColumnSettingPopup(column) {
            if (column == undefined) {
                column = {
                    groupID: this.selectedGroup._id,
                    order: this.columns?.length ?? 0
                }
            }
            showColumnSetting(column, this.handleColumnUpdate)
        },
        // 담당자 저장
        async saveReservationColumns() {
            try {
                if (this.isSaving) { return }

                this.isLoading = true
                this.isSaving = true

                await this.requestUpdateReservationColumnSort()

                this.setToast({
                    isShow: true,
                    text: this.$t('common.label_save_success')
                })

                this.reqeustFetchReservationSetting()

            } catch (err) {
                console.log('failed to save reservation groups' , err)
            } finally {
                this.isLoading = false
                this.isSaving = false
            }
        },
        // 그룹 목록 조회
        async reqeustFetchReservationGroup() {
            try {

                // 봉투 에러(code != 0)는 ApiError로 throw — 아래 catch에서 로그 (기존 동작 동일)
                const groups = await this.queryClient.fetchQuery(v2ReservationGroupListQueryOptions(this.hospital.selectedHospital._id))
                this.originalGroups = groups?.filter(g => !g.isAi)
            } catch (err) {
                console.log("reservation group find error: " ,err)
            }
        },
        // 담당자 목록 조회
        async requestFetchReservationColumns() {
            try {
                if (this.originalGroups == undefined || this.originalGroups.length == 0) {
                    return
                }
                
                this.isLoading = true

                const param = {
                    hospitalID: this.hospital.selectedHospital._id,
                    groupID: this.originalGroups.map( g => g._id),
                    isRemoved: false
                }

                // 봉투 에러(code != 0)는 ApiError로 throw — 아래 catch에서 로그 (기존 throw Error(result) 동일)
                const foundColumns = await this.queryClient.fetchQuery(reservationColumnFindQueryOptions(param))
                this.groupByColumn = foundColumns.reduce((acc, obj) => {
                    if (!acc[obj.groupID]) {
                        acc[obj.groupID] = [obj]
                    } else {
                        acc[obj.groupID].push(obj)
                        acc[obj.groupID].sort( (l, r) => l.sortNum - r.sortNum)
                    }
                    return acc
                }, {})

                this.selectedGroup = this.originalGroups[0]
            } catch (err) {
                console.error("reservation columns find error", err)
            } finally {
                this.isLoading = false
            }
        },
        // 담당자 수정
        async requestUpdateReservationColumnSort() {
            let ids = Object.values(this.groupByColumn)
                .flatMap(array => array.map( (column, index) => ({
                    _id: column._id,
                    title: column.title,
                    sortNum: index
            })))

            let data = {
                columns: ids
            }
            // 봉투 에러(code != 0)는 ApiError로 throw — 호출자에게 전파 (기존 throw Error와 동일)
            await this.updateColumnSort(data)
            this.$emit('groupUpdateDone')
        },
        // 예약설정 조회
        async reqeustFetchReservationSetting() {
            try {
                this.isLoading = true
                let param = {
                    hospitalID: this.hospital.selectedHospital._id,
                }
                // 봉투 에러(code != 0)는 ApiError로 throw — 아래 catch에서 로그 (기존 동작 동일)
                this.settingValue = await this.queryClient.fetchQuery(v2ReservationSettingFindOneQueryOptions(param))
            } catch (err) {
                console.log('reservation setting find error from group management component ', err)
            } finally {
                this.isLoading = false
            }
        },

        async handleColumnUpdate() {
            this.reqeustFetchReservationSetting()

            let oldOne = this.selectedGroup
            await this.reqeustFetchReservationGroup()
            await this.requestFetchReservationColumns()
            this.selectedGroup = oldOne
        }
    }
}
</script>